/* eslint-disable @typescript-eslint/dot-notation */
import { FC, useEffect, useState } from 'react';
import { Col, Dropdown, Empty, Input, Row, Space, Typography } from 'antd';
import { CloseCircleOutlined, Loading3QuartersOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import axios, { AxiosResponse } from 'axios';
import SimpleBar from 'simplebar-react';
import StatusTag from 'components/StatusTag';
import apiRoutes from 'config/apiRoute';
import appRoutes from 'config/appRoutes';
import { convertSnakeCaseToTitleCase, includesInObject } from 'utils/string.utils';
import apiRequests from 'utils/api';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import { debounceAsync } from 'utils/debounce';
import styles from './GlobalSearch.module.less';

const mapSearchTypeUrl = {
  projects: (item: any) => `${appRoutes.PROJECTS}/${item.uuid}`,
  tasks: (item: any) => `?task_uuid=${item.task_uuid}`,
  people: (item: any) => `${appRoutes.CONTACTS}/${item.uuid}/edit`,
  cms: (item: any) => `${appRoutes.CONTENT}/${item.uuid}/edit`,
  clients: (item: any) => `?client_uuid=${item.uuid}`,
  requests: (item: any) => `${appRoutes.REQUESTS}/${item.uuid}`,
};

const { Text, Title } = Typography;

const debounceGetSearch = debounceAsync<AxiosResponse>((params: any) => {
  return apiRequests.get(apiRoutes.GLOBAL_SEARCH, params);
}, 300);

interface GlobalSearchProps {}

const GlobalSearch: FC<GlobalSearchProps> = () => {
  const [state, setState] = useState({ loading: false, data: null as any, search: null as any, visible: false });

  const { loading, data, search, visible } = state;
  const filteredData: any = {};
  Object.keys(data ?? {})?.forEach((el: any) => {
    filteredData[el] = data?.[el]?.filter((item: any) => {
      return includesInObject(search, item);
    });
  });
  const hasData = Object.keys(data ?? {})?.find((el: any) => data?.[el]?.length !== 0);

  const toggleVisible = () => {
    setState((prevState) => ({ ...prevState, visible: !prevState.visible }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setState((prevState: any) => ({ ...prevState, loading: true }));

      try {
        const res = await debounceGetSearch({
          search_term: search,
        });

        setState((prevState: any) => ({ ...prevState, loading: false, data: res?.data?.data }));
      } catch (error) {
        if (!axios.isCancel(error)) {
          asyncErrorHandler(error);
          setState((prevState: any) => ({ ...prevState, loading: false }));
        }
      }
    };

    fetchData();
  }, [search]);

  return (
    <Dropdown
      visible={visible}
      overlay={
        <div className={styles.overlay}>
          {!hasData ? (
            <div style={{ padding: 30 }}>
              <Empty />
            </div>
          ) : (
            <>
              <SimpleBar style={{ maxHeight: 400 }}>
                {Object.keys(filteredData ?? {}).map((type: string) => {
                  const parsedData = filteredData?.[type];
                  const getUrl = mapSearchTypeUrl[type as keyof typeof mapSearchTypeUrl];

                  if (parsedData?.length === 0 || !getUrl) {
                    return null;
                  }

                  return (
                    <div className={styles.item} key={type}>
                      <Title level={5} className="mb-m">
                        {convertSnakeCaseToTitleCase(type)}
                      </Title>

                      {parsedData?.map((item: any, key: any) => {
                        return (
                          <Link
                            key={key}
                            to={getUrl(item)}
                            className={styles.data}
                          >
                            <Row wrap={false} justify="space-between" gutter={10}>
                              <Col span={item.status || item.task_status ? 18 : 24}>
                                <Text ellipsis>{item.business_name ?? item.title ?? item.name ?? item.task_title}</Text>

                                {type === 'requests' && (
                                  <Text className="font-bold" style={{ paddingLeft: '4px' }} ellipsis>
                                    - {item.subject}
                                  </Text>
                                )}
                              </Col>

                              {(item.status || item.task_status) && (
                                <Col>
                                  <StatusTag value={item.status ?? item.task_status} />
                                </Col>
                              )}
                            </Row>

                            {(type === 'projects' || type === 'tasks' || type === 'requests') && (
                              <Row>
                                <Col span={24}>
                                  <Text style={{ fontSize: 12 }} className="primary" ellipsis>
                                    {item.customer_business_name ?? item.project?.customer?.business_name}
                                  </Text>
                                </Col>
                              </Row>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  );
                })}
              </SimpleBar>
            </>
          )}
        </div>
      }
    >
      <div className={styles.inputWrapper}>
        <Input
          placeholder="Search"
          value={search}
          onFocus={toggleVisible}
          onBlur={() => {
            setTimeout(() => toggleVisible(), 300);
          }}
          onChange={(e) => {
            setState((prevState) => ({ ...prevState, search: e.target.value }));
          }}
          size="large"
          className={styles.input}
          suffix={
            <Space>
              <Loading3QuartersOutlined style={{ visibility: loading ? 'visible' : 'hidden' }} spin size={20} />
              {search && (
                <CloseCircleOutlined
                  className="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setState((prevState) => ({ ...prevState, search: undefined }));
                  }}
                />
              )}
            </Space>
          }
        />
      </div>
    </Dropdown>
  );
};

export default GlobalSearch;
